import { z } from 'zod';

export const MARKET_CONSOLE_PIPELINES = [
  'STOCKBEE_BREADTH',
  'CFTC_POSITIONING',
  'STOCK_LEADERS',
  'ETF_FLOW',
  'ETF_EXTENDED_HOURS',
] as const;
export type MarketConsolePipeline = (typeof MARKET_CONSOLE_PIPELINES)[number];

export function isMarketConsolePipeline(value: string): value is MarketConsolePipeline {
  return MARKET_CONSOLE_PIPELINES.some((pipeline) => pipeline === value);
}

/** A past session to replay; omitted means live. */
export const ReplayDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Not a valid date.')
  .refine((value) => value <= new Date().toISOString().slice(0, 10), 'Date is in the future.');

export const ReplayQuerySchema = z.object({ asOf: ReplayDateSchema.optional() });
export type ReplayQuery = z.infer<typeof ReplayQuerySchema>;

export interface StockbeeBreadthRowDto {
  date: string;
  up4: number | null;
  down4: number | null;
  ratio5: number | null;
  ratio10: number | null;
  up25Quarter: number | null;
  down25Quarter: number | null;
  up25Month: number | null;
  down25Month: number | null;
  up50Month: number | null;
  down50Month: number | null;
  up13In34Days: number | null;
  down13In34Days: number | null;
  universe: number | null;
  t2108: number | null;
  spClose: number | null;
}

export type BreadthPressureStateDto = 'BUYING_PRESSURE' | 'SELLING_PRESSURE' | 'MIXED';

export interface MarketPulseDto {
  status: 'READY' | 'NOT_AVAILABLE';
  asOfDate: string | null;
  fetchedAt: string | null;
  pressure: { state: BreadthPressureStateDto; reason: string } | null;
  latest: StockbeeBreadthRowDto | null;
  sincePriorSession: {
    priorDate: string;
    net4Change: number | null;
    t2108Change: number | null;
    ratio10Change: number | null;
  } | null;
  /** Newest first. */
  rows: StockbeeBreadthRowDto[];
  source: { name: string; url: string };
  informationalOnly: true;
}

export type CotParticipantKeyDto = 'commercials' | 'largeSpeculators' | 'smallTraders';

export interface CotParticipantDto {
  long: number;
  short: number;
  net: number;
  rangeIndex: number | null;
  netPctOpenInterest: number | null;
}

export type CotCrowdingDto = 'CROWDED_LONG' | 'CROWDED_SHORT' | null;

export interface CotIndexPointDto {
  date: string;
  commercials: number | null;
  largeSpeculators: number | null;
  smallTraders: number | null;
}

export interface CotMarketDto {
  code: string;
  name: string;
  proxyEtf: string;
  group: string;
  contractName: string;
  latestReportDate: string;
  openInterest: number;
  reports: number;
  participants: Record<CotParticipantKeyDto, CotParticipantDto>;
  crowding: CotCrowdingDto;
  /** Oldest first. */
  history: CotIndexPointDto[];
}

export interface MarketCrowdingDto {
  status: 'READY' | 'NOT_AVAILABLE';
  lookbackWeeks: number;
  extremeThreshold: number;
  fetchedAt: string | null;
  markets: CotMarketDto[];
  unavailableMarkets: string[];
  source: { name: string; url: string };
  informationalOnly: true;
}
